import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { CURRENCY } from "@src/utility/constants/panel/common";

const DEFAULT_LIMIT = 10;

const useBrandDashboard = (brandId) => {
    const { axiosInstance, loading } = useAxios({ base: "brand", initialLoader: true });
    const currency = CURRENCY;

    const [summary, setSummary] = useState(null);
    const [publishers, setPublishers] = useState([]);
    const [publishersPagination, setPublishersPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT });
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [paymentHistoryPagination, setPaymentHistoryPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT });

    const fetchSummary = async () => {
        try {
            const res = await axiosInstance.get(`v1/accounts/${brandId}/dashboard/summary`);
            const result = res.data || res;
            setSummary(result.data || result);
        } catch {
            setSummary(null);
        }
    };

    const fetchPublishers = async (page = 1, limit = DEFAULT_LIMIT) => {
        try {
            const res = await axiosInstance.get(`v1/accounts/${brandId}/dashboard/publishers`, { params: { page, limit } });
            const result = res.data || res;
            const items = result.items || result.data || result.results || [];
            setPublishers(items);
            setPublishersPagination({
                total: result.total ?? items.length,
                page : result.page ?? page,
                limit: result.limit ?? limit
            });
        } catch {
            setPublishers([]);
            setPublishersPagination({ total: 0, page, limit });
        }
    };

    const fetchPaymentHistory = async (page = 1, limit = DEFAULT_LIMIT) => {
        try {
            const res = await axiosInstance.get(`v1/accounts/${brandId}/dashboard/payment-history`, { params: { page, limit } });
            const result = res.data || res;
            const items = result.items || result.data || result.results || [];
            setPaymentHistory(items);
            setPaymentHistoryPagination({
                total: result.total ?? items.length,
                page : result.page ?? page,
                limit: result.limit ?? limit
            });
        } catch {
            setPaymentHistory([]);
            setPaymentHistoryPagination({ total: 0, page, limit });
        }
    };

    useEffect(() => {
        if (!brandId) return;
        fetchSummary();
        fetchPublishers(1, DEFAULT_LIMIT);
        fetchPaymentHistory(1, DEFAULT_LIMIT);
    }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePublishersPageChange = (limit, page) => fetchPublishers(page, limit);
    const handlePublishersRowsChange = (limit) => fetchPublishers(1, limit);
    const handlePaymentHistoryPageChange = (limit, page) => fetchPaymentHistory(page, limit);
    const handlePaymentHistoryRowsChange = (limit) => fetchPaymentHistory(1, limit);

    return {
        loading,
        currency,
        summary,
        publishers,
        publishersPagination,
        handlePublishersPageChange,
        handlePublishersRowsChange,
        paymentHistory,
        paymentHistoryPagination,
        handlePaymentHistoryPageChange,
        handlePaymentHistoryRowsChange
    };
};

export default useBrandDashboard;
